import { createSelector } from '@ngrx/store';
import { docsFeature } from '../docs/docs.reducer';
import { docKey } from '../docs/docs.normalize';
import { selectActiveProjectId } from '../workspace/workspace.selectors';
import { ticketsFeature } from './tickets.reducer';
import { ChatMessage, RepoContext, extractDiagram, extractSchema, normalizeRepoContext } from './tickets.models';

const { selectEntities, selectKeyIndex } = docsFeature;
const {
  selectBoard,
  selectDirty,
  selectSaving,
  selectSaveError,
  selectLastSavedAt,
  selectLoading,
  selectError,
  selectExecutionModelId,
} = ticketsFeature;

export {
  selectBoard as selectTicketsBoard,
  selectDirty as selectTicketsDirty,
  selectSaving as selectTicketsSaving,
  selectSaveError as selectTicketsSaveError,
  selectLastSavedAt as selectTicketsLastSavedAt,
  selectLoading as selectTicketsLoading,
  selectError as selectTicketsError,
  selectExecutionModelId as selectTicketsExecutionModelId,
};

export const selectHasBoard = createSelector(selectBoard, (b) => (b?.length ?? 0) > 0);

function dataFor(
  entities: ReturnType<typeof selectEntities>,
  keyIndex: Record<string, string>,
  pid: string | null,
  key: string
): any {
  if (!pid) return null;
  const id = keyIndex[docKey(pid, key)];
  return id ? entities.entities[id]?.data ?? null : null;
}

export const selectTicketsChatMessages = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): ChatMessage[] => {
    const d = dataFor(e, k, pid, 'tickets.chat');
    return Array.isArray(d?.messages) ? (d.messages as ChatMessage[]) : [];
  }
);

export const selectTicketsProjectDefinition = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): any => dataFor(e, k, pid, 'define.definition')
);

export const selectTicketsProjectSchema = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): any => extractSchema(dataFor(e, k, pid, 'db-design.main'))
);

export const selectTicketsProjectDiagram = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): any => extractDiagram(dataFor(e, k, pid, 'architecture.main'))
);

export const selectTicketsRepoContext = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): RepoContext | null => normalizeRepoContext(dataFor(e, k, pid, 'repos.settings'))
);
